import {
  LayoutDashboard, Search as SearchIcon, Users, Newspaper, MessageSquare, Bell, Heart,
  CalendarDays, Pencil, CreditCard, LifeBuoy, Settings as SettingsIcon, Megaphone, Mic,
  Briefcase, Building2, Wrench, ListChecks, Inbox, ShieldCheck, CheckCircle2, Camera, Star,
  CalendarRange, Cloud, Palette, Upload, FileText, ShieldAlert, UserPlus, TrendingUp, Archive,
  Activity, BarChart3, Calendar as CalendarIcon, Mail, Sparkles,
} from 'lucide-react';

export function getActiveRole(user) {
  if (!user) return null;
  return user.active_role || user.role || null;
}

export function isAdminOrStaff(user) {
  if (!user) return false;
  if (user.role === 'admin' || user.active_role === 'admin') return true;
  if (Array.isArray(user.roles) && user.roles.includes('admin')) return true;
  return !!(user.is_staff || user.is_master_admin);
}

function canNavigateToEventPosting(user) {
  if (!user) return false;
  const role = getActiveRole(user);
  if (role === 'admin') return true;
  if (role === 'casting_professional' || role === 'production_company') return true;
  if (user.lifetime_free === true) return true;
  if (!['talent', 'agent', 'crew', 'industry_service'].includes(role)) return false;
  return ['active', 'past_due', 'trialing'].includes(String(user.subscription_status || '').toLowerCase());
}

const ITEM_HOME = { id: 'home', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard?tab=overview' };
const ITEM_CASTING_CALLS = { id: 'casting-calls', label: 'Casting Calls', icon: SearchIcon, to: '/casting-calls' };
const ITEM_FEATURED = { id: 'featured', label: 'Featured', icon: Star, to: '/featured' };
const ITEM_TALENT_DIR = { id: 'talent', label: 'Talent Directory', icon: Users, to: '/talent' };
const ITEM_NEWS = { id: 'news', label: 'News & Advice', icon: Newspaper, to: '/news' };
const ITEM_MESSAGES = { id: 'messages', label: 'Messages', icon: MessageSquare, to: '/messages', badgeKey: 'messages' };
const ITEM_NOTIFICATIONS = { id: 'notifications', label: 'Notifications', icon: Bell, to: '/notifications', badgeKey: 'notifications' };
const ITEM_SAVED = { id: 'saved', label: 'Saved', icon: Heart, to: '/saved' };
const ITEM_NETWORK = { id: 'network', label: 'Network', icon: UserPlus, to: '/dashboard?tab=connections' };
const ITEM_PLANNER = { id: 'planner', label: 'Planner', icon: CalendarDays, to: '/dashboard?tab=planner' };
const ITEM_POST_EVENT = { id: 'post-event', label: 'Post an Event', icon: CalendarRange, to: '/events?manage=1' };
const ITEM_EDIT_PROFILE = { id: 'edit-profile', label: 'Edit Profile', icon: Pencil, to: '/dashboard?tab=profile' };
const ITEM_MEMBERSHIP_BILLING = { id: 'membership-billing', label: 'Account & Billing', icon: CreditCard, to: '/account/billing' };
const ITEM_SETTINGS = { id: 'settings', label: 'Settings', icon: SettingsIcon, to: '/account/settings?anchor=account-settings' };
const ITEM_SUPPORT = { id: 'support', label: 'Help & Support', icon: LifeBuoy, to: '/support' };
const ITEM_MY_TICKETS = { id: 'my-tickets', label: 'My Tickets', icon: Inbox, to: '/account/support' };

const DISCOVER_ITEMS = [
  ITEM_CASTING_CALLS, ITEM_FEATURED, ITEM_TALENT_DIR,
  { id: 'casting-professionals', label: 'Casting Professionals', icon: Camera, to: '/casting-professionals' },
  { id: 'agents', label: 'Agents', icon: Briefcase, to: '/agents' },
  { id: 'companies', label: 'Production Companies', icon: Building2, to: '/companies' },
  { id: 'services', label: 'Industry Services', icon: Wrench, to: '/services' },
  { id: 'crew', label: 'Crew Directory', icon: Star, to: '/crew' },
  { id: 'events', label: 'Events', icon: CalendarRange, to: '/events' },
  ITEM_NEWS,
];

function commonMainSection(extraMainItems = []) {
  return {
    id: 'main', title: 'My Dashboard', icon: LayoutDashboard, inlineDropdown: true,
    items: [ITEM_HOME, ITEM_EDIT_PROFILE, ITEM_MESSAGES, ITEM_NOTIFICATIONS, ITEM_NETWORK, ITEM_PLANNER, ITEM_SAVED, ...extraMainItems,
      { id: 'dashboard-account-divider', label: 'Account', divider: true }, ITEM_SETTINGS, ITEM_MEMBERSHIP_BILLING],
  };
}

function commonDiscoverSection() {
  return { id: 'discover', title: 'Discover', inlineDropdown: true, items: DISCOVER_ITEMS };
}

function commonSupportLegalSection() {
  return {
    id: 'support-legal', title: 'Support & Legal', collapsible: true,
    items: [
      ITEM_SUPPORT, ITEM_MY_TICKETS,
      { id: 'legal-terms', label: 'Terms of Service', icon: FileText, to: '/terms' },
      { id: 'legal-privacy', label: 'Privacy Policy', icon: ShieldAlert, to: '/privacy' },
      { id: 'legal-cookies', label: 'Cookie Policy', icon: FileText, to: '/cookie-policy' },
      { id: 'legal-guidelines', label: 'Community Guidelines', icon: FileText, to: '/guidelines' },
    ],
  };
}

function castingProWorkspaceSection() {
  return {
    id: 'workspace', title: 'Casting Workspace',
    items: [
      { id: 'post-casting-call', label: 'Post Casting Call', icon: Megaphone, to: '/casting-calls/new' },
      { id: 'castflow-ai', label: 'CastFlow AI', icon: Sparkles, to: '/casting-calls/new?mode=ai' },
      { id: 'my-listings', label: 'My Casting Calls', icon: ListChecks, to: '/dashboard?tab=listings' },
      { id: 'manage-applications', label: 'Manage Applications', icon: Inbox, to: '/dashboard?tab=applicants' },
    ],
  };
}

function agentWorkspaceSection() {
  return { id: 'workspace', title: 'Agency', items: [{ id: 'my-roster', label: 'My Roster', icon: Users, to: '/dashboard?tab=roster' }] };
}
function industryServiceWorkspaceSection() { return { id: 'workspace', title: 'Services', items: [] }; }
function crewWorkspaceSection() { return { id: 'workspace', title: 'Crew', items: [{ id: 'crew-directory', label: 'Crew Directory', icon: Star, to: '/crew' }] }; }

function adminWorkspaceSection() {
  return {
    id: 'admin', title: 'Site Management', icon: ShieldCheck, inlineDropdown: true,
    popoutLabel: 'More Admin Tools',
    dailyItemIds: ['admin-dashboard','admin-planner','admin-analytics','admin-users','admin-approvals','admin-team','admin-broadcast','admin-email-centre'],
    items: [
      { id: 'admin-dashboard', label: 'Admin Dashboard', icon: ShieldCheck, to: '/dashboard?tab=overview' },
      { id: 'admin-planner', label: 'Admin Planner', icon: CalendarDays, to: '/dashboard?tab=planner' },
      { id: 'admin-analytics', label: 'Admin Analytics', icon: BarChart3, to: '/dashboard?tab=analytics' },
      { id: 'group-users-mod', label: 'Users & Moderation', divider: true },
      { id: 'admin-users', label: 'Users', icon: Users, to: '/dashboard?tab=users' },
      { id: 'admin-approvals', label: 'Approvals', icon: CheckCircle2, to: '/dashboard?tab=approvals' },
      { id: 'admin-archived', label: 'Archived Users', icon: Archive, to: '/dashboard?tab=archived' },
      { id: 'admin-listing-claims', label: 'Listing Claims', icon: ShieldCheck, to: '/dashboard?tab=listing-claims' },
      { id: 'admin-messenger-moderation', label: 'Messenger Moderation', icon: ShieldCheck, to: '/dashboard?tab=messenger-moderation' },
      { id: 'admin-audition-moderation', label: 'Audition Rooms Reports', icon: ShieldCheck, to: '/dashboard?tab=audition-moderation' },
      { id: 'group-content', label: 'Content & Listings', divider: true },
      { id: 'admin-content', label: 'Content', icon: Building2, to: '/dashboard?tab=content' },
      { id: 'admin-news-editor', label: 'News Editor', icon: Newspaper, to: '/dashboard?tab=content&view=news' },
      { id: 'admin-featured-engagement', label: 'Featured Engagement', icon: TrendingUp, to: '/dashboard?tab=overview' },
      { id: 'group-comms', label: 'Communications', divider: true },
      { id: 'admin-broadcast', label: 'Bulk Messaging', icon: Megaphone, to: '/dashboard?tab=content&view=broadcast' },
      { id: 'admin-email-centre', label: 'Email & Notifications', icon: Mail, to: '/dashboard?tab=email-centre' },
      { id: 'admin-team', label: 'Team & Promotions', icon: UserPlus, to: '/dashboard?tab=team' },
      { id: 'admin-support', label: 'Support', icon: LifeBuoy, to: '/dashboard?tab=support' },
      { id: 'group-billing', label: 'Billing & Churn', divider: true },
      { id: 'admin-cancellations', label: 'Cancellations', icon: Archive, to: '/dashboard?tab=cancellations' },
      { id: 'group-system', label: 'System Tools', divider: true },
      { id: 'admin-theme-editor', label: 'Theme / Colour Editor', icon: Palette, to: '/dashboard?tab=content&view=toolkit' },
      { id: 'admin-import-export', label: 'Import / Export', icon: Upload, to: '/dashboard?tab=content&view=import-export' },
      { id: 'admin-backup', label: 'Drive Backup', icon: Cloud, to: '/dashboard?tab=content&view=import-export#backup' },
      { id: 'admin-maintenance', label: 'Maintenance', icon: SettingsIcon, to: '/dashboard?tab=maintenance' },
      { id: 'admin-status', label: 'Status', icon: Activity, to: '/dashboard?tab=status' },
      { id: 'admin-settings', label: 'System Settings', icon: SettingsIcon, to: '/dashboard?tab=settings' },
    ],
  };
}

const ROLE_WORKSPACE_TITLE = {
  talent: 'My Toolkit', casting_professional: 'Casting Studio', production_company: 'Production Hub',
  agent: 'Agency Hub', industry_service: 'Service Hub', crew: 'Crew Toolkit', admin: 'Site Management',
};

const ROLE_DASHBOARD_EXTRA_TABS = {
  talent: [
    { id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' },
    { id: 'workspace-audition-rooms', label: 'Audition Rooms', icon: Mic, to: '/audition-rooms' },
    { id: 'workspace-teleprompter', label: 'Teleprompter', icon: FileText, to: '/teleprompter' },
  ],
  casting_professional: [
    { id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' },
    { id: 'workspace-audition-rooms', label: 'Audition Rooms', icon: Mic, to: '/audition-rooms' },
    { id: 'workspace-teleprompter', label: 'Teleprompter', icon: FileText, to: '/teleprompter' },
  ],
  production_company: [
    { id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' },
    { id: 'workspace-audition-rooms', label: 'Audition Rooms', icon: Mic, to: '/audition-rooms' },
    { id: 'workspace-teleprompter', label: 'Teleprompter', icon: FileText, to: '/teleprompter' },
  ],
  agent: [
    { id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' },
    { id: 'workspace-audition-rooms', label: 'Audition Rooms', icon: Mic, to: '/audition-rooms' },
    { id: 'workspace-teleprompter', label: 'Teleprompter', icon: FileText, to: '/teleprompter' },
  ],
  industry_service: [{ id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' }],
  crew: [{ id: 'workspace-projects', label: 'Projects', icon: Megaphone, to: '/dashboard?tab=projects' }],
};

function roleDashboardExtras(activeRole) { return ROLE_DASHBOARD_EXTRA_TABS[activeRole] || []; }
function dedupeByTo(items) { const seen=new Set(); const out=[]; for(const it of items){const key=(it.to||'').split('#')[0]; if(!key||seen.has(key))continue; seen.add(key); out.push(it);} return out; }
function dashboardToolGroup(activeRole, items=[]) { const cleanItems=dedupeByTo(items.filter(Boolean)); if(!cleanItems.length)return []; return [{id:`${activeRole||'role'}-toolkit-divider`,label:ROLE_WORKSPACE_TITLE[activeRole]||'My Toolkit',divider:true},...cleanItems]; }

function sectionsForActiveRole(activeRole) {
  switch (activeRole) {
    case 'talent': {
      const dashboardTools=dashboardToolGroup('talent',[{id:'talent-toolkit-home',label:'Toolkit Home',icon:Wrench,to:'/toolkit'},{id:'my-applications',label:'My Applications',icon:ListChecks,to:'/dashboard?tab=applications'},...roleDashboardExtras('talent')]);
      return [commonMainSection(dashboardTools),commonDiscoverSection(),commonSupportLegalSection()];
    }
    case 'casting_professional':
    case 'production_company': {
      const workspace=castingProWorkspaceSection(); const dashboardTools=dashboardToolGroup(activeRole,[...workspace.items,...roleDashboardExtras(activeRole)]);
      return [commonMainSection(dashboardTools),commonDiscoverSection(),commonSupportLegalSection()];
    }
    case 'agent': {
      const workspace=agentWorkspaceSection(); const dashboardTools=dashboardToolGroup('agent',[...workspace.items,...roleDashboardExtras('agent')]);
      return [commonMainSection(dashboardTools),commonDiscoverSection(),commonSupportLegalSection()];
    }
    case 'industry_service': {
      const workspace=industryServiceWorkspaceSection(); const dashboardTools=dashboardToolGroup('industry_service',[...workspace.items,...roleDashboardExtras('industry_service')]);
      return [commonMainSection(dashboardTools),commonDiscoverSection(),commonSupportLegalSection()];
    }
    case 'crew': {
      const workspace=crewWorkspaceSection(); const dashboardTools=dashboardToolGroup('crew',[...workspace.items,...roleDashboardExtras('crew')]);
      return [commonMainSection(dashboardTools),commonDiscoverSection(),commonSupportLegalSection()];
    }
    case 'admin': {
      const baseMain=commonMainSection(); const adminMain={...baseMain,items:baseMain.items.filter(it=>it.id!=='planner')};
      return [adminMain,adminWorkspaceSection(),commonDiscoverSection(),commonSupportLegalSection()];
    }
    default: return [commonMainSection(),commonDiscoverSection(),commonSupportLegalSection()];
  }
}

export function getDesktopSidebar(user) {
  const activeRole=getActiveRole(user);
  const sections=sectionsForActiveRole(activeRole);
  return sections.map(section=>{
    let items=section.items.filter(item=>!item.hidden);
    if(section.id==='main'&&canNavigateToEventPosting(user)){
      const insertAt=Math.max(0,items.findIndex(item=>item.id==='edit-profile')+1);
      const copy=[...items];
      if(!copy.some(item=>item.id==='post-event')) copy.splice(insertAt,0,ITEM_POST_EVENT);
      items=copy;
    }
    return {...section,items};
  }).filter(section=>section.items.length>0);
}

const EXTRA_MOBILE_LEGAL = [
  { id:'legal-cookies',label:'Cookie Policy',icon:FileText,to:'/cookie-policy' },
  { id:'legal-guidelines',label:'Community Guidelines',icon:FileText,to:'/guidelines' },
];

export function getMobileDrawer(user) {
  const desktop=getDesktopSidebar(user);
  const mainSec=desktop.find(s=>s.id==='main'); const adminSec=desktop.find(s=>s.id==='admin'); const discoverSec=desktop.find(s=>s.id==='discover'); const accountSec=desktop.find(s=>s.id==='account'); const supportLegal=desktop.find(s=>s.id==='support-legal');
  const dashboardItems=mainSec?mainSec.items:[]; const siteMgmtItems=adminSec?adminSec.items:[]; const discoverItems=discoverSec?discoverSec.items:[]; const accountItems=accountSec?accountSec.items:[];
  const helpItems=supportLegal?supportLegal.items.filter(it=>!it.id.startsWith('legal-')):[];
  const legalItems=supportLegal?dedupeByTo([...supportLegal.items.filter(it=>it.id.startsWith('legal-')),...EXTRA_MOBILE_LEGAL]):[...EXTRA_MOBILE_LEGAL];
  const sections=[];
  if(dashboardItems.length)sections.push({id:'my-hub',title:'My Dashboard',items:dashboardItems,collapsible:true,defaultOpen:true});
  if(discoverItems.length)sections.push({id:'discover',title:'Discover',items:discoverItems,collapsible:true});
  if(siteMgmtItems.length)sections.push({id:'site-mgmt',title:'Site Management',items:siteMgmtItems,collapsible:true});
  if(accountItems.length)sections.push({id:'account',title:'Account',items:accountItems,collapsible:true});
  if(helpItems.length)sections.push({id:'help',title:'Help & Support',items:helpItems,collapsible:true});
  if(legalItems.length)sections.push({id:'legal',title:'Legal',items:legalItems,collapsible:true});
  return sections;
}

